import React, { useState, useEffect } from 'react';
import { Modal, View, Text, TextInput, TouchableOpacity, StyleSheet } from 'react-native';
import { BACKEND_URL } from '../../../../Config/Const';
import { colores } from '../../../../styles/variablesCss';
import RevalidateEmailModal from './RevalidateEmailModal';
import { trackCompleteRegistration } from '../../../../utils/analytics';

const VerificationCodeModal = ({ visible, onClose, mail: propMail, route, navigation }) => {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showRevalidateModal, setShowRevalidateModal] = useState(false);
  const [internalVisible, setInternalVisible] = useState(visible);

  const urlMail = route?.params?.mail;
  const urlCode = route?.params?.cod;
  const mail = propMail || urlMail;

  useEffect(() => {
    setInternalVisible(visible);

    // Cada vez que se abre el modal, reseteamos estados para evitar que queden pegados
    // (por ejemplo, success=true por una verificación anterior).
    if (visible) {
      setError('');
      setIsLoading(false);
      setSuccess(false);
      setShowRevalidateModal(false);

      // El input del código se resetea salvo que venga precargado por URL.
      setCode(urlCode || '');
    }
  }, [visible]);

  const closeModal = (result = false) => {
    setInternalVisible(false);
    setShowRevalidateModal(false);
    if (typeof onClose === 'function') {
      onClose(result);
    } else if (navigation?.navigate) {
      navigation.navigate('');
    }
  };

  const verifyCode = async (mailToVerify, codeToVerify) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/rest/usuarios/validar-mail?mail=${mailToVerify}&cod=${codeToVerify}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (response.ok && data.dsResultado?.resultado?.[0]?.resultado) {
        trackCompleteRegistration({ email: mailToVerify });
        setSuccess(true);
        setTimeout(() => {
          closeModal(true);
        }, 2000);
      } else {
        setError(data.dsResultado?.resultado?.[0]?.mensaje || 'Código inválido');
      }
    } catch (error) {
      console.error('Error al verificar código:', error);
      setError('Error de conexión');
    } finally {
      setIsLoading(false);
      setShowRevalidateModal(false);
    }
  };

  // Establecer el código de la URL en el input si existe
  useEffect(() => {
    if (urlCode) {
      setCode(urlCode);
    }
  }, [urlCode]);

  const handleVerify = () => {
    if (!code) {
      setError('Por favor ingrese el código de verificación');
      return;
    }
    verifyCode(mail, code);
  };

  // Si no tenemos mail (ni por props ni por URL), mostramos error
  if (!mail) {
    return (
      <Modal
        visible={internalVisible}
        transparent={true}
        animationType="slide"
        onRequestClose={() => closeModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.container}>
              <Text style={styles.errorText}>Error: No se proporcionó un correo electrónico válido</Text>
              <TouchableOpacity
                style={styles.button}
                onPress={() => closeModal(false)}
              >
                <Text style={styles.buttonText}>Volver</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    );
  }

  return (
    <>
      <Modal
        visible={internalVisible}
        transparent={true}
        animationType="slide"
        onRequestClose={() => closeModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <TouchableOpacity
              style={styles.closeButton}
              onPress={() => closeModal(false)}
              hitSlop={{ top: 10, right: 10, bottom: 10, left: 10 }}
              accessibilityRole="button"
              accessibilityLabel="Cerrar"
            >
              <Text style={styles.closeButtonText}>×</Text>
            </TouchableOpacity>
            <View style={styles.container}>
              {success ? (
                <Text style={styles.successText}>¡Cuenta verificada correctamente!</Text>
              ) : (
                <View style={styles.contentRow}>
                  {/* Columna izquierda con copy solicitado */}
                  <View style={styles.leftInfo}>
                    <Text style={styles.leftTitle}>¡Revisá tu correo!</Text>
                    <Text style={styles.leftText}>Te enviamos un código de 6 dígitos
para confirmar tu cuenta.</Text>
                  </View>

                  {/* Columna derecha con formulario */}
                  <View style={styles.rightForm}>
                    <Text style={styles.description}>
                      Te enviamos un código de verificación a tu correo: {mail}
                    </Text>

                    <TextInput
                      style={styles.inputPill}
                      placeholder="Ingresá el código"
                      value={code}
                      onChangeText={setCode}
                      keyboardType="numeric"
                      editable={!isLoading}
                      placeholderTextColor="#A0A0A0"
                    />

                    {error ? <Text style={styles.errorText}>{error}</Text> : null}

                    <TouchableOpacity
                      style={styles.button}
                      onPress={handleVerify}
                      disabled={isLoading}
                    >
                      <Text style={styles.buttonText}>
                        {isLoading ? 'Verificando...' : 'Verificar'}
                      </Text>
                    </TouchableOpacity>

                    <TouchableOpacity
                      style={styles.revalidateButton}
                      onPress={() => {
                        setShowRevalidateModal(true);
                        setError('');
                      }}
                    >
                      <Text style={styles.revalidateText}>Revalidar</Text>
                    </TouchableOpacity>

                    {isLoading && (
                      <Text style={styles.description}>Verificando código...</Text>
                    )}
                  </View>
                </View>
              )}
            </View>
          </View>
        </View>
      </Modal>

      <RevalidateEmailModal
        visible={showRevalidateModal}
        mail={mail}
        onClose={(success) => {
          setShowRevalidateModal(false);
          if (success === true) {
            setSuccess(true);
          }
        }}
      />
    </>
  );
};

const styles = StyleSheet.create({
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: 'white',
    borderRadius: 10,
    padding: 20,
    width: '92%',
    maxWidth: 720,
  },
  container: {
    position: 'relative',
  },
  closeButton: {
    position: 'absolute',
    right: -5,
    top: -18,
    padding: 10,
    zIndex: 10000,
    cursor: 'pointer',
  },
  closeButtonText: {
    fontSize: 24,
    color: colores.rojo || '#E31837',
    fontWeight: 'bold',
  },
  title: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 20,
    textAlign: 'center',
  },
  description: {
    fontSize: 16,
    marginBottom: 15,
    textAlign: 'center',
    color: '#666',
  },
  // Input estilo pill
  inputPill: {
    height: 42,
    width: '100%',
    backgroundColor: '#FFFFFF',
    borderRadius: 21,
    paddingHorizontal: 16,
    borderWidth: 1,
    borderColor: '#E5E5E5',
    fontSize: 15,
    marginBottom: 15,
  },
  button: {
    backgroundColor: '#E31837',
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 6,
    alignItems: 'center',
    alignSelf: 'center',
    minWidth: 180,
  },
  buttonText: {
    color: 'white',
    fontSize: 18,
    fontWeight: '700',
  },
  errorText: {
    color: 'red',
    marginBottom: 10,
  },
  successText: {
    color: 'green',
    fontSize: 18,
    marginBottom: 10,
    textAlign: 'center',
  },
  // Layout 2 columnas
  contentRow: {
    flexDirection: 'row',
    gap: 16,
    alignItems: 'stretch',
  },
  leftInfo: {
    flex: 1,
    justifyContent: 'center',
    paddingRight: 8,
  },
  leftTitle: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#E30613',
    marginBottom: 8,
    textAlign: 'left',
  },
  leftText: {
    lineHeight: 20,
    whiteSpace: 'pre-line',
    color: '#333',
    fontSize: 18,
  },
  rightForm: {
    flex: 1,
  },
  revalidateButton: {
    marginTop: 10,
  },
  revalidateText: {
    textAlign: 'center',
    color: '#9E9E9E',
    fontSize: 12,
    textDecorationLine: 'underline',
  },
});

export default VerificationCodeModal;
